import { FORMATION_RADIUS } from "./visualBounds.ts";
import { seeded } from "../utils/math.ts";
import type { FormationId } from "../utils/types.ts";

export function fillFormation(
  id: FormationId,
  count: number,
  targets: Float32Array,
  bases: Float32Array,
): void {
  for (let i = 0; i < count; i += 1) {
    const p = particleFor(id, i, count);
    const o = i * 3;
    targets[o] = p[0];
    targets[o + 1] = p[1];
    targets[o + 2] = p[2];
  }
  normalizeToRadius(targets, count, FORMATION_RADIUS);
  bases.set(targets);
}

export function maxRadius(targets: Float32Array, count: number): number {
  let max = 0;
  for (let i = 0; i < count; i += 1) {
    const o = i * 3;
    const r = Math.hypot(targets[o] ?? 0, targets[o + 1] ?? 0, targets[o + 2] ?? 0);
    if (r > max) max = r;
  }
  return max;
}

function normalizeToRadius(targets: Float32Array, count: number, radius: number): void {
  const current = maxRadius(targets, count);
  if (current < 0.001) return;
  const scale = radius / current;
  for (let i = 0; i < count * 3; i += 1) {
    targets[i] = (targets[i] ?? 0) * scale;
  }
}

function particleFor(id: FormationId, i: number, count: number): [number, number, number] {
  const u = i / count;
  const r1 = seeded(i);
  const r2 = seeded(i + 91);
  const r3 = seeded(i + 173);
  switch (id) {
    case "spiral-galaxy":
      return spiral(u, r1, r2, i);
    case "saturn":
      return saturn(u, r1, r2, r3);
    case "heart":
      return heart(u, r1, r2);
    case "dna-helix":
      return dna(u, r1, i);
    case "star-sphere":
      return starSphere(u, r1, r2, r3);
    case "accretion-disc":
      return accretion(u, r1, r2);
    default:
      return spiral(u, r1, r2, i);
  }
}

function spiral(u: number, r1: number, r2: number, i: number): [number, number, number] {
  const arms = 3;
  const arm = i % arms;
  const radius = u * 4.2 + r1 * 0.18;
  const theta = radius * 2.35 + (arm * Math.PI * 2) / arms + r2 * 0.12;
  const y = (r1 - 0.5) * 0.28 * Math.exp(-radius * 0.18);
  return [Math.cos(theta) * radius, y, Math.sin(theta) * radius];
}

function saturn(u: number, r1: number, r2: number, r3: number): [number, number, number] {
  const tilt = 0.32;
  if (u < 0.34) {
    const phi = Math.acos(2 * r1 - 1);
    const theta = 2 * Math.PI * r2;
    const radius = 1.2 + r3 * 0.08;
    const x = Math.sin(phi) * Math.cos(theta) * radius;
    const y = Math.cos(phi) * radius;
    const z = Math.sin(phi) * Math.sin(theta) * radius;
    return [x, y * Math.cos(tilt) - z * Math.sin(tilt), y * Math.sin(tilt) + z * Math.cos(tilt)];
  }
  const radius = 1.85 + (u - 0.34) * 2.6 + (r1 - 0.5) * 0.1;
  const theta = r2 * Math.PI * 2;
  const x = Math.cos(theta) * radius;
  const y = (r3 - 0.5) * 0.08;
  const z = Math.sin(theta) * radius;
  return [x, y * Math.cos(tilt) - z * Math.sin(tilt), y * Math.sin(tilt) + z * Math.cos(tilt)];
}

function heart(u: number, r1: number, r2: number): [number, number, number] {
  const t = u * Math.PI * 2;
  const x = 16 * Math.sin(t) ** 3;
  const y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
  return [
    (x / 16) * 2.2 + (r1 - 0.5) * 0.12,
    (y / 16) * 2.2 + (r2 - 0.5) * 0.12,
    (r1 - 0.5) * 0.45,
  ];
}

function dna(u: number, r1: number, i: number): [number, number, number] {
  const y = (u - 0.5) * 5.4;
  const radius = 1.05;
  if (i % 9 === 0) {
    const t = y * 2.35;
    const a = r1;
    return [
      Math.cos(t) * radius * (1 - a) + Math.cos(t + Math.PI) * radius * a,
      y,
      Math.sin(t) * radius * (1 - a) + Math.sin(t + Math.PI) * radius * a,
    ];
  }
  const strand = i % 2;
  const theta = y * 2.35 + strand * Math.PI;
  return [Math.cos(theta) * radius, y, Math.sin(theta) * radius];
}

function starSphere(u: number, r1: number, r2: number, r3: number): [number, number, number] {
  const phi = Math.acos(1 - 2 * u);
  const theta = Math.PI * (1 + Math.sqrt(5)) * (u * 4000);
  const spike = r1 > 0.92 ? 1.8 + r2 * 1.4 : 1;
  const radius = (1.7 + r3 * 0.15) * spike;
  return [
    Math.sin(phi) * Math.cos(theta) * radius,
    Math.cos(phi) * radius,
    Math.sin(phi) * Math.sin(theta) * radius,
  ];
}

function accretion(u: number, r1: number, r2: number): [number, number, number] {
  const radius = 0.55 + u * 3.4;
  const theta = r1 * Math.PI * 2 + u * 18;
  const y = (r2 - 0.5) * (0.22 + (1 / (radius + 0.2)) * 0.08);
  return [Math.cos(theta) * radius, y, Math.sin(theta) * radius];
}
